
import { defineComponent, PropType, computed, ref } from "vue";
import store, { EditStore } from "@/stores";
import {
  consts,
  ErrorMessageFilter,
  IErrorResponse,
  isErrors,
  IImageMaterial,
  isImage,
  MaterialRights,
  MaterialTypes,
} from "@/services/Models";
import Thumbnail from "@/components/Thumbnail.vue";
import {
  dragDataKeyImageObject,
  toResourceUrl,
  readAsDataURL,
  makeImage,
  isIncludeMIMEType,
} from "@/lib/shared";
import { AxiosResponse } from "axios";

const acceptMediaTypes = [
  "image/png",
  "image/jpeg",
  "image/pjpeg",
  "application/pjpeg",
  "image/bmp",
  "image/gif",
];

export default defineComponent({
  name: "EditCommandUserImages",
  components: {
    Thumbnail,
  },
  props: {
    editStore: { type: Object as PropType<EditStore> },
  },
  setup(props) {
    const resource = store.useResource();
    // ユーザがアップロードした画像素材
    const privateMaterials = computed(() => {
      return resource.data.imageMaterials.filter(
        (x) => x.right == MaterialRights.User && x.type == MaterialTypes.Image
      );
    });

    const isShowConfirm = ref(false);
    const selectedItem = ref<IImageMaterial>();
    const toItem = (image: IImageMaterial) => ({
      imgPath: toResourceUrl(image.path),
      clickAction: () => {
        selectedItem.value = image;
      },
    });
    // データをJSON形式に変換し、ドラッグで指定した位置にコピーする
    const dragstart = (e: DragEvent, item: IImageMaterial) => {
      if (!e.dataTransfer) return;
      const data = JSON.stringify(item);
      e.dataTransfer.setData(dragDataKeyImageObject, data);
      e.dataTransfer.effectAllowed = "copy";
    };
    const remove = async (material: IImageMaterial) => {
      if (material.id === undefined) return;
      await resource.deleteUserImageMaterial(material.id);
      // 編集画面から削除したイメージを削除
      props.editStore?.clearObjects((item) => {
        if (!item) return false;
        if (!isImage(item)) return false;
        return item.image.id == material.id;
      });
      isShowConfirm.value = false;
    };
    const beforeRemove = () => {
      isShowConfirm.value = true;
    };

    // ファイル読み込み処理
    const fileElement = ref<HTMLInputElement>();
    const selectImage = async (file: File) => {
      if (fileElement.value) {
        fileElement.value.value = "";
      }
      const base64Img = (await readAsDataURL(file).catch(
        () => undefined
      )) as string;

      // 読み込み確認
      if (base64Img === undefined) {
        alert(consts.uploadMaterialSizeError);
        return;
      }
      // 拡張子チェック
      const isAcceptType = isIncludeMIMEType(base64Img, acceptMediaTypes);
      if (!isAcceptType) {
        alert(consts.uploadMaterialNotImage);
        return;
      }
      // 画像かどうかをチェック
      const isImage = await makeImage(base64Img).catch(() => undefined);
      if (isImage === undefined) {
        alert(consts.uploadMaterialNotImage);
        return;
      }

      const nameParts = file.name.split(".");
      let name = file.name;
      if (1 <= nameParts.length) {
        // 最後の拡張子だけ除いてその他のドットはファイル名として扱う
        nameParts.splice(nameParts.length - 1, 1);
        name = nameParts.join(".");
      }

      // 素材データをDBへアップロードする
      await resource
        .uploadUserImageMaterial(base64Img, name)
        .catch((ex: AxiosResponse) => {
          if (isErrors(ex.data)) {
            const errors = ex.data as IErrorResponse[];
            const messages = ErrorMessageFilter(errors, "DataURI");
            alert(messages.join(", "));
          } else {
            alert(consts.uploadMaterialFailure);
          }
        });
    };
    return {
      privateMaterials,
      toItem,
      selectedItem,
      dragstart,
      fileElement,
      selectImage,
      acceptMediaTypes,
      remove,
      beforeRemove,
      isShowConfirm,
    };
  },
});
